import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Stack,
  Autocomplete,
  TextField,
  Select,
  MenuItem,
  Slider,
  Button,
  Collapse,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Link as ForeignKeyIcon,
} from '@mui/icons-material';

interface ColumnSummary {
  name: string;
  semantic_role: string;
  description: string;
  pii_likelihood: number;
  is_primary_key?: boolean;
  is_foreign_key?: boolean;
  fk_references?: string;
  data_type?: string;
}

interface JoinDefinition {
  on: string[];
  to: string;
  join_type?: string;
  confidence?: number;
  description?: string;
}

interface TableSummary {
  file_name: string;
  original_name?: string;
  semantic_name: string;
  description: string;
  entity_type: string;
  primary_key_candidates?: string[];
  foreign_key_candidates?: string[];
  likely_joins?: JoinDefinition[];
  column_summaries: ColumnSummary[];
}

interface RelationshipEditorProps {
  tableIndex: number;
  tableName: string;
  joins: JoinDefinition[];
  allTables: TableSummary[];
  currentTableColumns: ColumnSummary[];
  onChange: (tableIndex: number, updatedJoins: JoinDefinition[]) => void;
}

const JOIN_TYPES = ['fk_candidate', 'pk_candidate', 'shared_key', 'semantic_link'];

const emptyJoin = (): JoinDefinition => ({
  on: [],
  to: '',
  join_type: 'fk_candidate',
  confidence: 0.7,
  description: '',
});

const RelationshipEditor: React.FC<RelationshipEditorProps> = ({
  tableIndex,
  tableName,
  joins,
  allTables,
  currentTableColumns,
  onChange,
}) => {
  const [addingNew, setAddingNew] = useState(false);
  const [newJoin, setNewJoin] = useState<JoinDefinition>(emptyJoin());
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editJoin, setEditJoin] = useState<JoinDefinition>(emptyJoin());

  const otherTables = allTables.filter((t) => t.file_name !== tableName);
  const columnOptions = currentTableColumns.map((c) => c.name);

  const getTargetColumns = (targetTableName: string): string[] => {
    const target = allTables.find(
      (t) => t.file_name === targetTableName || t.semantic_name === targetTableName
    );
    return target ? (target.column_summaries || []).map((c) => c.name) : [];
  };

  const handleDelete = (index: number) => {
    const updated = joins.filter((_, i) => i !== index);
    onChange(tableIndex, updated);
  };

  const handleAdd = () => {
    if (!newJoin.to || newJoin.on.length === 0) return;
    onChange(tableIndex, [...joins, newJoin]);
    setNewJoin(emptyJoin());
    setAddingNew(false);
  };

  const handleStartEdit = (index: number) => {
    setEditingIndex(index);
    setEditJoin({ ...joins[index] });
    setAddingNew(false);
  };

  const handleSaveEdit = () => {
    if (editingIndex === null) return;
    const updated = [...joins];
    updated[editingIndex] = editJoin;
    onChange(tableIndex, updated);
    setEditingIndex(null);
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
  };

  const formatJoinType = (type?: string) => {
    if (!type) return 'shared_key';
    return type.replace(/_/g, ' ');
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <ForeignKeyIcon fontSize="small" color="info" /> Relationships
      </Typography>

      {joins.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Join Column(s)</TableCell>
                <TableCell>Target Table</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="center">Confidence</TableCell>
                <TableCell align="center" sx={{ width: 80 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {joins.map((join, index) => (
                <React.Fragment key={`${join.to}-${join.on.join(',')}-${index}`}>
                  {editingIndex === index ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ p: 2 }}>
                        <JoinForm
                          join={editJoin}
                          onChange={setEditJoin}
                          columnOptions={columnOptions}
                          otherTables={otherTables}
                          getTargetColumns={getTargetColumns}
                        />
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          <Button size="small" variant="contained" onClick={handleSaveEdit}>
                            Save
                          </Button>
                          <Button size="small" onClick={handleCancelEdit}>
                            Cancel
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow hover>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
                          {join.on.map((col) => (
                            <Chip key={col} label={col} size="small" variant="outlined" />
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{join.to}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={formatJoinType(join.join_type)}
                          size="small"
                          color={join.join_type === 'fk_candidate' || join.join_type === 'pk_candidate' ? 'info' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography
                          variant="body2"
                          color={
                            (join.confidence || 0) > 0.8
                              ? 'success.main'
                              : (join.confidence || 0) > 0.5
                              ? 'warning.main'
                              : 'text.secondary'
                          }
                        >
                          {((join.confidence || 0) * 100).toFixed(0)}%
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0} justifyContent="center">
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleStartEdit(index)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => handleDelete(index)} color="error">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {joins.length === 0 && !addingNew && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          No relationships defined.
        </Typography>
      )}

      <Collapse in={addingNew}>
        <Paper variant="outlined" sx={{ p: 2, mb: 1 }}>
          <Typography variant="body2" fontWeight="medium" sx={{ mb: 1.5 }}>
            New Relationship
          </Typography>
          <JoinForm
            join={newJoin}
            onChange={setNewJoin}
            columnOptions={columnOptions}
            otherTables={otherTables}
            getTargetColumns={getTargetColumns}
          />
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <Button
              size="small"
              variant="contained"
              onClick={handleAdd}
              disabled={!newJoin.to || newJoin.on.length === 0}
            >
              Add
            </Button>
            <Button
              size="small"
              onClick={() => {
                setAddingNew(false);
                setNewJoin(emptyJoin());
              }}
            >
              Cancel
            </Button>
          </Stack>
        </Paper>
      </Collapse>

      {!addingNew && (
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => {
            setAddingNew(true);
            setEditingIndex(null);
          }}
          variant="text"
        >
          Add Relationship
        </Button>
      )}
    </Box>
  );
};

// Inline form for adding/editing a join
interface JoinFormProps {
  join: JoinDefinition;
  onChange: (join: JoinDefinition) => void;
  columnOptions: string[];
  otherTables: TableSummary[];
  getTargetColumns: (tableName: string) => string[];
}

const JoinForm: React.FC<JoinFormProps> = ({
  join,
  onChange,
  columnOptions,
  otherTables,
  getTargetColumns,
}) => {
  const targetTableOptions = otherTables.map((t) => t.file_name);
  const targetColumns = join.to ? getTargetColumns(join.to) : [];

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2}>
        <Autocomplete
          multiple
          size="small"
          options={columnOptions}
          value={join.on}
          onChange={(_, newValue) => onChange({ ...join, on: newValue })}
          renderInput={(params) => (
            <TextField {...params} label="Join Column(s)" placeholder="Select columns..." />
          )}
          sx={{ flex: 1 }}
        />
        <Autocomplete
          size="small"
          options={targetTableOptions}
          value={join.to || null}
          onChange={(_, newValue) => onChange({ ...join, to: newValue || '' })}
          renderInput={(params) => (
            <TextField {...params} label="Target Table" placeholder="Select table..." />
          )}
          sx={{ flex: 1 }}
        />
      </Stack>
      <Stack direction="row" spacing={2} alignItems="center">
        {targetColumns.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 'fit-content' }}>
            Target columns: {targetColumns.slice(0, 5).join(', ')}{targetColumns.length > 5 ? ` +${targetColumns.length - 5} more` : ''}
          </Typography>
        )}
        <Select
          size="small"
          value={join.join_type || 'fk_candidate'}
          onChange={(e) => onChange({ ...join, join_type: e.target.value })}
          sx={{ minWidth: 140 }}
        >
          {JOIN_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              {type.replace(/_/g, ' ')}
            </MenuItem>
          ))}
        </Select>
        <Box sx={{ flex: 1, px: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Confidence: {((join.confidence || 0) * 100).toFixed(0)}%
          </Typography>
          <Slider
            size="small"
            value={(join.confidence || 0.7) * 100}
            onChange={(_, value) => onChange({ ...join, confidence: (value as number) / 100 })}
            min={0}
            max={100}
            sx={{ py: 0 }}
          />
        </Box>
      </Stack>
    </Stack>
  );
};

export default RelationshipEditor;
